<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    Textarea,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api, type TaskRecommendation } from '@/api'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import { addressLabel } from '@/lib/presentation'

const route = useRoute()
const rec = ref<TaskRecommendation | null>(null)
const error = ref<string | null>(null)
const busy = ref(false)
/** Decline opens a dialog that asks why first: the recommendation is kept with the reason. */
const declining = ref(false)
const reason = ref('')
let timer: ReturnType<typeof setInterval> | null = null

const taskId = computed(() => String(route.params.taskId ?? rec.value?.taskId ?? ''))
const canDecide = computed(() => rec.value?.status === 'pending' || rec.value?.status === 'error')

onMounted(() => {
    void load()
    timer = setInterval(() => {
        if (rec.value?.status === 'implementing') {
            void load()
        }
    }, 2000)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
})

watch(
    () => route.params.id,
    () => {
        void load()
    },
)

async function load(): Promise<void> {
    try {
        rec.value = await api.recommendation(String(route.params.id))
        error.value = null
    } catch {
        error.value = 'Recommendation not found.'
        rec.value = null
    }
}

async function decide(decision: 'approve' | 'decline'): Promise<void> {
    if (!rec.value || busy.value || (decision === 'approve' && rec.value.canImplement !== true)) {
        return
    }
    // #1796: approving starts the implement run, which is model work, so it asks for the AI account first. Declining
    // starts nothing and never asks.
    if (decision === 'approve' && !(await requireAiAccount())) {
        return
    }
    busy.value = true
    error.value = null
    try {
        rec.value = await api.decideRecommendation(
            rec.value.id,
            decision,
            decision === 'decline' ? reason.value.trim() : undefined,
        )
        declining.value = false
        reason.value = ''
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not ${decision} (${caught.message}).` : `Could not ${decision}.`
    } finally {
        busy.value = false
    }
}

/** Closing the dialog, by Cancel, Escape or the backdrop, drops the half-written reason with it. */
function cancelDecline(): void {
    declining.value = false
    reason.value = ''
}

function percent(value: number): string {
    return `${Math.round(value * 100)}%`
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/tasks">Tasks</RouterLink>
			<span v-if="taskId">
				·
				<RouterLink class="underline" :to="`/tasks/${taskId}`">Task</RouterLink>
			</span>
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<template v-if="rec">
			<div class="flex flex-wrap items-start justify-between gap-3">
				<div>
					<ZhPageTitle kicker="Recommendation">{{ rec.title }}</ZhPageTitle>
					<p class="text-muted-foreground text-sm">
						<ZhSystemLabel as="span">{{ rec.status }}</ZhSystemLabel> · confidence {{ percent(rec.confidence) }}
					</p>
				</div>
				<div v-if="canDecide" class="flex flex-wrap gap-2">
					<Button type="button" size="sm" :disabled="busy || rec.canImplement !== true" @click="decide('approve')">Approve</Button>
					<Button type="button" size="sm" variant="outline" :disabled="busy" @click="declining = true">
						Decline
					</Button>
				</div>
			</div>
			<ZhPanel v-if="canDecide && rec.canImplement !== true" as="p" measure="prose" class="text-sm">Only the owning enterprise can implement changes to this definition. You can still decline this recommendation, or <RouterLink class="underline" :to="`/tasks/${taskId}`">copy the task</RouterLink> to create your own editable version.</ZhPanel>
			<Dialog :open="canDecide && declining" @update:open="open => { if (!open) cancelDecline() }">
				<DialogContent class="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Why decline it?</DialogTitle>
						<DialogDescription>
							The recommendation is kept with your reason, so the same change is not proposed again.
						</DialogDescription>
					</DialogHeader>
					<div class="grid gap-2">
						<Label for="recommendation-decline-reason">Reason</Label>
						<Textarea
							id="recommendation-decline-reason"
							v-model="reason"
							rows="3"
							placeholder="For example: not reproduced in the source run, or already fixed"
						/>
					</div>
					<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
					<DialogFooter>
						<Button type="button" size="sm" variant="outline" @click="cancelDecline">Cancel</Button>
						<Button type="button" size="sm" :disabled="busy || !reason.trim()" @click="decide('decline')">
							Decline
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<ZhPanel v-if="rec.status !== 'pending' && rec.decidedAt" class="space-y-1 text-sm">
				<ZhSectionHeading>Decision</ZhSectionHeading>
				<p>
					<ZhSystemLabel as="span">{{ rec.status }}</ZhSystemLabel><span v-if="rec.lane"> · <ZhSystemLabel as="span">lane {{ rec.lane }}</ZhSystemLabel></span> ·
					{{ new Date(rec.decidedAt).toLocaleString() }} by
					<RouterLink v-if="rec.decidedRunId" class="underline" :to="`/runs/${rec.decidedRunId}`">
						a triage run
					</RouterLink>
					<span v-else>a person</span>
				</p>
				<p v-if="rec.issueUrl">
					<a class="underline" :href="rec.issueUrl" target="_blank" rel="noopener">{{
						addressLabel(rec.issueUrl)
					}}</a>
				</p>
				<p v-if="rec.decisionNote" class="whitespace-pre-wrap">{{ rec.decisionNote }}</p>
			</ZhPanel>
			<p class="text-sm">{{ rec.summary }}</p>
			<section class="space-y-2">
				<ZhSectionHeading>Why</ZhSectionHeading>
				<p class="whitespace-pre-wrap text-sm">{{ rec.rationale || '—' }}</p>
			</section>
			<section class="space-y-2">
				<ZhSectionHeading>Details</ZhSectionHeading>
				<p class="whitespace-pre-wrap text-sm">{{ rec.details }}</p>
			</section>
			<p v-if="rec.errorMessage" class="text-destructive text-sm">{{ rec.errorMessage }}</p>
			<p class="text-muted-foreground text-sm">
				Source run
				<RouterLink class="underline" :to="`/runs/${rec.sourceRunId}`">{{ rec.sourceRunId.slice(0, 8) }}</RouterLink>
				<span v-if="rec.implementRunId">
					· implement run
					<RouterLink class="underline" :to="`/runs/${rec.implementRunId}`">{{
						rec.implementRunId.slice(0, 8)
					}}</RouterLink>
				</span>
			</p>
		</template>
	</section>
</template>
